import { DEFAULT_FILTER } from './user';
import { AppUser } from 'Src/views/App/App';
import axios from 'Src/helpers/api/axiosInstance';
import { DEFAULT_LIMIT, DEFAULT_SKIP } from 'Src/consts/table';
import { Count } from '../../../models/count';

export interface ClubActivatePreviewResponse {
  startTimes: string[];
}

export type ClubsSportOption = { id: string; name: string };

export function getClubs(user: AppUser, filter = DEFAULT_FILTER) {
  const { roleSessionKey: key, activeSchoolId } = user;

  const config = {
    headers: { usid: key },
    params: {
      filter
    }
  };

  return axios.get(`${window.apiBase}/i/schools/${activeSchoolId}/clubs`, config).then(response => {
    return response.data;
  });
}

export function getAllClubs(user: AppUser, where?) {
  const { roleSessionKey: key, activeSchoolId } = user;

  let config;

  if (typeof where !== 'undefined') {
    config = {
      headers: { usid: key },
      params: {
        filter: {
          where: {
            ...where
          },
          limit: DEFAULT_LIMIT,
          skip: DEFAULT_SKIP
        }
      }
    };
  } else {
    config = {
      headers: { usid: key },
      params: {
        filter: {
          limit: DEFAULT_LIMIT,
          skip: DEFAULT_SKIP
        }
      }
    };
  }

  return axios.get(`${window.apiBase}/i/schools/${activeSchoolId}/clubs`, config).then(response => {
    return response.data;
  });
}

export function getClubsCount(user: AppUser, where?): Promise<Count> {
  const { roleSessionKey: key, activeSchoolId } = user;

  let config;

  if (typeof where !== 'undefined') {
    config = {
      headers: { usid: key },
      params: {
        filter: {
          where: where
        }
      }
    };
  } else {
    config = {
      headers: { usid: key }
    };
  }

  return axios.get<Count>(`${window.apiBase}/i/schools/${activeSchoolId}/clubs/count`, config).then(response => {
    return response.data;
  });
}

export function getClubsSports(user: AppUser, where?: any): Promise<ClubsSportOption[]> {
  const { roleSessionKey: key, activeSchoolId } = user;

  const config: any = {
    headers: { usid: key }
  };

  if (typeof where !== 'undefined') {
    config.params = {
      filter: {
        where
      }
    };
  }

  return axios
    .get(`${window.apiBase}/i/schools/${activeSchoolId}/clubs/sports`, config)
    .then(response => response.data);
}

export function createClub(user: AppUser, data) {
  const { roleSessionKey: key, activeSchoolId } = user;

  const config = {
    headers: { usid: key }
  };

  return axios.post(`${window.apiBase}/i/schools/${activeSchoolId}/clubs`, data, config).then(response => {
    return response.data;
  });
}

export function getClub(user: AppUser, clubId: string) {
  const { roleSessionKey: key, activeSchoolId } = user;

  const config = {
    headers: { usid: key }
  };

  return axios.get(`${window.apiBase}/i/schools/${activeSchoolId}/clubs/${clubId}`, config).then(response => {
    return response.data;
  });
}

export function updateClub(user: AppUser, data, clubId: string) {
  const { roleSessionKey: key, activeSchoolId } = user;

  const config = {
    headers: { usid: key }
  };

  return axios.put(`${window.apiBase}/i/schools/${activeSchoolId}/clubs/${clubId}`, data, config).then(response => {
    return response.data;
  });
}

export interface ActiveClubUpdatePlan {
  fingerprint: string;
  canApply: boolean;
  immutableChanges: string[];
  blockedPastEvents: Array<{ eventId: string; startTime: string }>;
  ageConflicts: Array<{ userId: string; permissionId: string; name: string; form: string; age: number }>;
  changedFields: { [key: string]: boolean };
  counts: {
    pastProtected: number;
    futureUpdated: number;
    futureRemoved: number;
    futureCreated: number;
    pastCreated: number;
    pastAvailableForBackfill: number;
  };
  eventsToUpdate: Array<{ eventId: string; startTime: string; newStartTime: string }>;
  eventsToRemove: Array<{ eventId: string; startTime: string }>;
  occurrencesToCreate: Array<{ startTime: string }>;
  pastOccurrencesToCreate: Array<{ startTime: string }>;
  isActive: boolean;
  oldScheduleStart?: string;
  newScheduleStart?: string;
}

const activeClubUpdateConfig = (
  user: AppUser,
  includePastOccurrences = false,
  previewFingerprint?: string,
  excludedOccurrenceStartTimes: string[] = []
) => ({
  headers: { usid: user.roleSessionKey },
  params: {
    timeZoneName: Intl.DateTimeFormat().resolvedOptions().timeZone,
    includePastOccurrences,
    previewFingerprint,
    excludedOccurrenceStartTimes: JSON.stringify(excludedOccurrenceStartTimes)
  }
});

export function previewActiveClubUpdate(
  user: AppUser,
  clubId: string,
  data: any,
  includePastOccurrences = false
): Promise<ActiveClubUpdatePlan> {
  return axios
    .post(
      `${window.apiBase}/i/schools/${user.activeSchoolId}/clubs/${clubId}/update-preview`,
      data,
      activeClubUpdateConfig(user, includePastOccurrences)
    )
    .then(response => response.data);
}

export function applyActiveClubUpdate(
  user: AppUser,
  clubId: string,
  data: any,
  includePastOccurrences = false,
  previewFingerprint?: string,
  excludedOccurrenceStartTimes: string[] = []
): Promise<{ club: any; plan: ActiveClubUpdatePlan }> {
  return axios
    .post(
      `${window.apiBase}/i/schools/${user.activeSchoolId}/clubs/${clubId}/apply-update`,
      data,
      activeClubUpdateConfig(user, includePastOccurrences, previewFingerprint, excludedOccurrenceStartTimes)
    )
    .then(response => response.data);
}

export function deleteClub(user: AppUser, clubId) {
  const { roleSessionKey: key, activeSchoolId } = user;

  const config = {
    headers: { usid: key }
  };

  return axios.delete(`${window.apiBase}/i/schools/${activeSchoolId}/clubs/${clubId}`, config).then(response => {
    return response.data;
  });
}

export interface ClubActivatePreviewResponse {
  startTimes: string[];
}

export function getClubActivatePreview(user: AppUser, clubId: string): Promise<ClubActivatePreviewResponse> {
  const { roleSessionKey: key, activeSchoolId } = user;
  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  const config = {
    headers: { usid: key },
    params: { timeZoneName: timeZone }
  };

  return axios
    .get<ClubActivatePreviewResponse>(
      `${window.apiBase}/i/schools/${activeSchoolId}/clubs/${clubId}/activate/preview`,
      config
    )
    .then(response => response.data);
}

export function activateClubWithOccurrences(user: AppUser, clubId: string, occurrences?: string[]) {
  const { roleSessionKey: key, activeSchoolId } = user;
  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  const data: any = { timeZoneName: timeZone };

  if (typeof occurrences !== 'undefined') {
    data.occurrences = occurrences;
  }

  const config = {
    headers: { usid: key }
  };

  return axios
    .post(`${window.apiBase}/i/schools/${activeSchoolId}/clubs/${clubId}/activate`, data, config)
    .then(response => response.data);
}

export function activateClub(user: AppUser, clubId: string) {
  const { roleSessionKey: key, activeSchoolId } = user,
    timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone,
    data = { timeZoneName: timeZone };

  const config = {
    headers: { usid: key }
  };

  return axios
    .post(`${window.apiBase}/i/schools/${activeSchoolId}/clubs/${clubId}/activate`, data, config)
    .then(response => {
      return response.data;
    });
}

export function updateClubBookingType(user: AppUser, clubId: string, isBookingFromWaitingList: boolean) {
  const { roleSessionKey: key, activeSchoolId } = user;
  const data = {
    isBookingFromWaitingList
  };

  const config = {
    headers: { usid: key }
  };

  return axios
    .post(`${window.apiBase}/i/schools/${activeSchoolId}/clubs/${clubId}/bookingType`, data, config)
    .then(response => {
      return response.data;
    });
}

export interface UpcomingEventsResponse {
  isUpcomingEventsExist: boolean;
  count: number;
}

export interface ClubArchivePreview {
  canArchive: boolean;
  alreadyArchived: boolean;
  clubStatus: string;
  participantCount: number;
  pastEventsCount: number;
  futureEvents: Array<{ eventId: string; startTime: string; endTime: string; status: string }>;
  linkedProgrammes: Array<{ id: string; name: string; status: string }>;
  fingerprint: string;
}

export function getClubArchivePreview(user: AppUser, clubId: string): Promise<ClubArchivePreview> {
  const { roleSessionKey: key, activeSchoolId } = user;
  return axios
    .get(`${window.apiBase}/i/schools/${activeSchoolId}/clubs/${clubId}/archive-preview`, {
      headers: { usid: key }
    })
    .then(response => response.data);
}

export function archiveClub(user: AppUser, clubId: string, fingerprint: string, reason?: string) {
  const { roleSessionKey: key, activeSchoolId } = user;
  return axios
    .post(
      `${window.apiBase}/i/schools/${activeSchoolId}/clubs/${clubId}/archive`,
      { fingerprint, reason: reason || undefined },
      { headers: { usid: key } }
    )
    .then(response => response.data);
}

export function restoreClubAsDraft(user: AppUser, clubId: string) {
  const { roleSessionKey: key, activeSchoolId } = user;
  return axios
    .post(`${window.apiBase}/i/schools/${activeSchoolId}/clubs/${clubId}/restore`, {}, { headers: { usid: key } })
    .then(response => response.data);
}

export function upcomingEvents(user: AppUser, clubId: string): Promise<UpcomingEventsResponse> {
  const { roleSessionKey: key, activeSchoolId } = user;
  const config = {
    headers: { usid: key }
  };

  return axios
    .post(`${window.apiBase}/i/schools/${activeSchoolId}/clubs/${clubId}/upcomingEvents`, {}, config)
    .then(response => {
      return {
        isUpcomingEventsExist: response.data.isUpcomingEventsExist,
        count: response.data.count
      };
    });
}

export function removeUpcomingEventsForClub(user: AppUser, clubId: string) {
  const { roleSessionKey: key, activeSchoolId } = user;

  const config = {
    headers: { usid: key }
  };

  return axios
    .post(`${window.apiBase}/i/schools/${activeSchoolId}/clubs/${clubId}/removeUpcomingEvents`, {}, config)
    .then(response => {
      return response.data;
    });
}

export function getCoaches(user: AppUser, filter) {
  const { roleSessionKey: key, activeSchoolId } = user;

  const config = {
    headers: { usid: key },
    params: {
      filter
    }
  };

  return axios.get(`${window.apiBase}/i/schools/${activeSchoolId}/users/coaches`, config).then(response => {
    return response.data;
  });
}

export function getStaffs(user: AppUser, filter) {
  const { roleSessionKey: key, activeSchoolId } = user;

  const config = {
    headers: { usid: key },
    params: {
      filter
    }
  };

  return axios.get(`${window.apiBase}/i/schools/${activeSchoolId}/users/staffs`, config).then(response => {
    return response.data;
  });
}

export function getAvailableClubSpacesMax(user: AppUser, clubId: string) {
  const { roleSessionKey: key, activeSchoolId } = user;

  const config = {
    headers: { usid: key }
  };

  return axios
    .get(`${window.apiBase}/i/schools/${activeSchoolId}/clubs/${clubId}/participants/available/max`, config)
    .then(response => {
      return response.data;
    });
}

export function cancelClubMessageOutdatedStatus(user: AppUser, clubId: string, messageIdList: string[]) {
  const { roleSessionKey: key, activeSchoolId } = user;

  const data = {
    messageIdList
  };

  const config = {
    headers: { usid: key }
  };

  return axios
    .put(`${window.apiBase}/i/schools/${activeSchoolId}/clubs/${clubId}/messages/outdated`, data, config)
    .then(response => {
      return response.data;
    });
}
